import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import {
  createDealStageAsset,
  deleteDealStageAsset,
  getDealStageAssets,
} from "actions/dealStageAsset";
import cx from "classnames";
import groupBy from "lodash/groupBy";
import includes from "lodash/includes";
import orderBy from "lodash/orderBy";
import AssetIcon from "rialto-icons/Asset";
import { assetNamesSelector } from "selectors/asset";
import { dealSelector, dealStageAssetsSelector } from "selectors/deal";
import { isAssetDealsEnabled as isAssetDealsEnabledSelector } from "selectors/limitations";

import Checkbox from "components/Checkbox";
import FormButton from "components/FormButton";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "components/Modal";
import StageProgress from "components/StageProgress";
// TODO: move me appropriately
import styles from "components/StageProgress/StageProgress.css";
import SubmitButton from "components/SubmitButton";
import Translate from "utils/translate";

const LIMIT = 3;

const { t } = new Translate("EnquiryHeader");

export function DealStages({ onSwitchStage }) {
  const { dealID } = useParams();
  const dispatch = useDispatch();
  const [editStage, setEditStage] = useState(null);

  const { assets, deal } = useSelector((state) => {
    const currentDeal = dealSelector(state);
    const allAssets = assetNamesSelector(state);

    return {
      assets: allAssets.filter((asset) => asset.portfolio.id === currentDeal.portfolio.id),
      deal: currentDeal,
    };
  });
  const dealStages = Object.values(deal.portfolio.dealStages || []);
  const dealStageAssets = useSelector(dealStageAssetsSelector);
  const isAssetDealsEnabled = useSelector(isAssetDealsEnabledSelector);

  const groupedDealStageAssets = useMemo(
    () => groupBy(orderBy(dealStageAssets, "asset.name"), "stage"),
    [dealStageAssets]
  );

  function handleSwitchStage(stage) {
    // TODO: Feature Flag
    onSwitchStage(
      stage,
      (groupedDealStageAssets[stage] || groupedDealStageAssets[deal.stage] || []).map(
        (dealStageAsset) => dealStageAsset.asset.id
      )
    );
  }

  useEffect(() => {
    dispatch(getDealStageAssets(dealID));
  }, [dealID, dispatch]);

  return (
    <>
      <StageProgress
        onSwitch={handleSwitchStage}
        selectedStage={deal.stage}
        availableStages={dealStages}
        dealStageAssets={dealStageAssets}
      />
      {isAssetDealsEnabled && (
        <>
          <div className="flex">
            {dealStages.map((stage) => (
              <DealStageAssets
                assets={groupedDealStageAssets[stage] || []}
                stage={stage}
                onEdit={() => setEditStage(stage)}
                key={stage}
              />
            ))}
          </div>
          {editStage && (
            <DealStageAssetModal
              stage={editStage}
              dealStage={deal.stage}
              onClose={() => setEditStage(null)}
              stageAssets={editStage ? groupedDealStageAssets[editStage] || [] : []}
              assets={assets}
            />
          )}
        </>
      )}
    </>
  );
}

function DealStageAssets({ assets, onEdit, stage }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const limitedAssets = assets.slice(0, LIMIT);
  const visibleAssets = isExpanded ? assets : limitedAssets;

  return (
    <div className={styles.dealStageAssets} key={stage}>
      <div>
        {visibleAssets.map((stageAsset) => (
          <div className={styles.asset} key={stageAsset.id}>
            <AssetIcon />
            {stageAsset.asset && stageAsset.asset.name}
          </div>
        ))}
      </div>
      {assets.length > LIMIT && (
        <button className={styles.btnExpand} onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded
            ? t("DealStages.hide")
            : t("DealStages.show", { smart_count: assets.length - LIMIT })}
        </button>
      )}
      <div className={styles.btnEdit}>
        <FormButton type="edit" onClick={onEdit} />
      </div>
    </div>
  );
}

function DealStageAssetModal({ assets, dealStage, onClose, stage, stageAssets }) {
  const { dealID } = useParams();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAssetIDs, setSelectedAssetIDs] = useState(
    stageAssets.map((stageAsset) => stageAsset.asset.id)
  );

  function handleClick(assetID) {
    if (selectedAssetIDs.some((id) => id === assetID)) {
      return setSelectedAssetIDs(selectedAssetIDs.filter((id) => id !== assetID));
    }

    return setSelectedAssetIDs([...selectedAssetIDs, assetID]);
  }

  // TODO: optimistic?
  function onSubmit() {
    setIsSubmitting(true);
    const stageAssetsIDs = stageAssets.map((stageAsset) => stageAsset.asset.id);

    const deletePromises = stageAssets
      .filter((stageAsset) => !includes(selectedAssetIDs, stageAsset.asset.id))
      .map(({ assetId, id }) =>
        dispatch(deleteDealStageAsset({ id, assetId, dealId: dealID, stage }))
      );

    const createPromises = selectedAssetIDs
      .filter((assetID) => !includes(stageAssetsIDs, assetID))
      .map((assetId) => dispatch(createDealStageAsset({ assetId, dealId: dealID, stage })));

    if (stage === dealStage) {
      dispatch({
        type: "DEAL_UPDATE_ASSETS",
        meta: {
          assetIDs: selectedAssetIDs,
        },
      });
    }

    // TODO: refetch deal spaces
    Promise.all([...deletePromises, ...createPromises])
      .then(() => {
        setIsSubmitting(false);
        onClose();
      })
      .catch((error) => {
        setIsSubmitting(false);
        console.error(error);
      });
  }

  return (
    <Modal isOpen onClose={isSubmitting ? null : onClose}>
      <ModalHeader onClose={isSubmitting ? null : onClose}>
        {t("DealStages.modal.title", { stage: t(`dealStages.${stage}`) })}
      </ModalHeader>
      <ModalBody isInfo>
        {t("DealStages.modal.body", { stage: t(`dealStages.${stage}`) })}
      </ModalBody>
      <ModalBody>
        <div>
          {assets.map((asset) => {
            const isSelected = selectedAssetIDs.some((assetID) => assetID === asset.id);

            return (
              <button
                className={cx(styles.item, {
                  [styles.selected]: isSelected,
                })}
                type="button"
                onClick={() => handleClick(asset.id)}
              >
                <Checkbox isChecked={isSelected} />
                {asset.name}
              </button>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        {/* TODO: translate labels */}
        <SubmitButton isSubmitting={isSubmitting} onSubmit={onSubmit} />
      </ModalFooter>
    </Modal>
  );
}
